import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { authEvents } from '../utils/api';

const ErrorNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [openNotification, setOpenNotification] = useState(null);

  // 通知を表示する関数
  const showNotification = (notification) => {
    // 同じメッセージがすでにあれば追加しない
    if (notifications.some(n => n.message === notification.message)) {
      return;
    }

    // 新しい通知を追加
    const newNotification = {
      ...notification,
      id: Date.now(), // ユニークID
      timestamp: notification.timestamp || new Date().toISOString(),
      // デフォルト値を設定
      severity: notification.severity || getErrorSeverity(notification),
      autoHideDuration: notification.autoHideDuration || 6000
    };

    setNotifications(prev => [...prev, newNotification]);
    
    // 通知がまだ表示されていなければ、この通知を表示
    if (!openNotification) {
      setOpenNotification(newNotification);
    }
  };

  // エラータイプから重大度を判定
  const getErrorSeverity = (error) => {
    if (!error) return 'error';
    
    if (error.errorType === 'network' || error.errorType === 'offline') {
      return 'warning';
    } else if (error.errorType === 'server') {
      return 'error';
    } else if (error.errorType === 'client' || error.errorType === 'validation') {
      return 'info';
    } else if (error.errorType === 'fallback') {
      return 'info';
    }
    return 'error';
  };

  // 通知を閉じる
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    if (openNotification) {
      // 現在の通知を閉じる
      setOpenNotification(null);
      
      // キューから削除
      setNotifications(prev => 
        prev.filter(n => n.id !== openNotification.id)
      );
      
      // キューに通知があれば次を表示
      setTimeout(() => {
        const nextNotifications = notifications.filter(
          n => n.id !== openNotification.id
        );
        if (nextNotifications.length > 0) {
          setOpenNotification(nextNotifications[0]);
        }
      }, 300);
    }
  };

  // 再試行アクション
  const handleRetry = () => {
    if (openNotification && openNotification.retryAction) {
      openNotification.retryAction();
    }
    handleClose();
  };

  // APIエラーイベントのリスナー
  useEffect(() => {
    const handleApiError = (error) => {
      showNotification({
        message: error.message || 'APIエラーが発生しました',
        severity: 'error',
        errorType: error.errorType,
        autoHideDuration: 6000
      });
    };

    const handleNetworkError = (error) => {
      showNotification({
        message: error.message || 'ネットワークエラーが発生しました',
        severity: 'warning',
        errorType: 'network',
        autoHideDuration: 10000,
        retryAction: error.retry ? () => {
          // 再試行のためにページをリロード
          window.location.reload();
        } : null
      });
    };

    // イベントリスナーを登録
    authEvents.on('apiError', handleApiError);
    authEvents.on('networkError', handleNetworkError);

    return () => {
      // コンポーネントのアンマウント時にリスナーを削除
      const listeners = authEvents.listeners;
      if (listeners.apiError) {
        const index = listeners.apiError.indexOf(handleApiError);
        if (index !== -1) listeners.apiError.splice(index, 1);
      }
      
      if (listeners.networkError) {
        const index = listeners.networkError.indexOf(handleNetworkError);
        if (index !== -1) listeners.networkError.splice(index, 1);
      }
    };
  }, []);

  if (!openNotification) {
    return null;
  }

  return (
    <Snackbar
      open={!!openNotification}
      autoHideDuration={openNotification?.autoHideDuration || 6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        elevation={6}
        variant="filled"
        onClose={handleClose}
        severity={openNotification?.severity || 'error'}
        sx={{ width: '100%' }}
        action={
          openNotification?.retryAction ? (
            <Button color="inherit" size="small" onClick={handleRetry}>
              再試行
            </Button>
          ) : undefined
        }
      >
        {openNotification?.message}
      </Alert>
    </Snackbar>
  );
};

export default ErrorNotification; 